import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Card, Select, Row, Col, Spin, Space, Empty } from "antd";
import {
    PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { CalendarOutlined } from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";

const { Option } = Select;

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const now = new Date();
const CURRENT_YEAR = now.getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);

const COLORS = ["#0e497b", "#10b981", "#d97706", "#ef4444", "#8b5cf6", "#06b6d4"];

const LEAVE_DEFS = [
    { key: "total_cl",         label: "Casual Leave",    abbr: "CL",  color: "#0e497b", bg: "#eff6ff", border: "#bfdbfe" },
    { key: "total_sl",         label: "Sick Leave",      abbr: "SL",  color: "#10b981", bg: "#f0fdf4", border: "#bbf7d0" },
    { key: "total_el",         label: "Earned Leave",    abbr: "EL",  color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
    { key: "total_lop",        label: "Loss of Pay",     abbr: "LOP", color: "#ef4444", bg: "#fff1f2", border: "#fecaca" },
    { key: "total_permission", label: "Permission Hours",abbr: "Perm",color: "#8b5cf6", bg: "#faf5ff", border: "#ddd6fe" },
];

export default function OrganisationLeave() {
    const { authData, backUrl } = useContext(AuthContext);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [year, setYear] = useState(CURRENT_YEAR);
    const [organisations, setOrganisations] = useState([]);
    const [organisationId, setOrganisationId] = useState("ALL");

    useEffect(() => {
        axios.get(`${backUrl}/api/emp/organisations/`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => setOrganisations(res.data.results || res.data || []))
          .catch(() => {});
    }, [backUrl, authData?.access]);

    useEffect(() => {
        setLoading(true);
        const params = { month, year };
        if (organisationId !== "ALL") params.organisation_id = organisationId;

        axios.get(`${backUrl}/api/reports/organisation-leave-summary/`, {
            params,
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => setData(res.data || null))
          .catch(() => setData(null))
          .finally(() => setLoading(false));
    }, [organisationId, month, year, backUrl, authData?.access]);

    const chartData = data
        ? LEAVE_DEFS
            .map(def => ({ name: def.abbr, fullName: def.label, value: data[def.key] || 0 }))
            .filter(i => i.value > 0)
        : [];

    const totalLeave = chartData.reduce((s, d) => s + d.value, 0);

    return (
        <Card
            bordered={false}
            style={{
                borderRadius: 16, background: "#ffffff",
                boxShadow: "0 4px 16px rgba(15,23,42,0.04)",
                border: "1px solid #e2e8f0", minHeight: 450,
            }}
            bodyStyle={{ padding: 20 }}
        >
            {/* Header */}
            <Row justify="space-between" align="middle" gutter={[0, 10]} style={{ marginBottom: 14 }}>
                <Col>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <CalendarOutlined style={{ color: "#0e497b", fontSize: 18 }} />
                        <div>
                            <div style={{ fontWeight: 800, fontSize: 15, color: "#0f172a" }}>
                                Organisation Leave Summary
                            </div>
                            <div style={{ fontSize: 12, color: "#64748b", marginTop: 1 }}>
                                Monthly leave breakdown &amp; permission logs
                            </div>
                        </div>
                    </div>
                </Col>
                <Col>
                    <Space size={6} wrap style={{ justifyContent: "flex-end" }}>
                        <Select value={organisationId} onChange={setOrganisationId} style={{ width: 150 }} size="small">
                            <Option value="ALL">All Orgs ({organisations.length})</Option>
                            {organisations.map(o => <Option key={o.id} value={o.id}>{o.name}</Option>)}
                        </Select>
                        <Select value={month} onChange={setMonth} style={{ width: 100 }} size="small">
                            {MONTHS.map((m, i) => <Option key={i + 1} value={i + 1}>{m}</Option>)}
                        </Select>
                        <Select value={year} onChange={setYear} style={{ width: 78 }} size="small">
                            {YEARS.map(y => <Option key={y} value={y}>{y}</Option>)}
                        </Select>
                    </Space>
                </Col>
            </Row>

            {loading ? (
                <div style={{ textAlign: "center", padding: "80px 0" }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 12, color: "#64748b", fontWeight: 600 }}>Loading Leave Breakdown...</div>
                </div>
            ) : !data || chartData.length === 0 ? (
                <Empty description="No leave records for selected month & organisation" style={{ padding: "80px 0" }} />
            ) : (
                <>
                    {/* Summary stat cards */}
                    <Row gutter={[8, 8]} style={{ marginBottom: 14 }}>
                        {LEAVE_DEFS.map(def => {
                            const val = data[def.key] || 0;
                            if (val === 0) return null;
                            return (
                                <Col xs={12} sm={8} md={4} key={def.key} style={{ flex: "1 1 auto" }}>
                                    <div style={{
                                        background: def.bg, border: `1px solid ${def.border}`,
                                        borderRadius: 10, padding: "8px 10px", textAlign: "center",
                                    }}>
                                        <div style={{ fontSize: 10, fontWeight: 700, color: def.color, textTransform: "uppercase", marginBottom: 2 }}>
                                            {def.abbr}
                                        </div>
                                        <div style={{ fontSize: 17, fontWeight: 900, color: def.color }}>{val}</div>
                                        <div style={{ fontSize: 10, color: "#64748b" }}>days</div>
                                    </div>
                                </Col>
                            );
                        })}
                        <Col xs={12} sm={8} md={4} style={{ flex: "1 1 auto" }}>
                            <div style={{
                                background: "#f8fafc", border: "1px solid #e2e8f0",
                                borderRadius: 10, padding: "8px 10px", textAlign: "center",
                            }}>
                                <div style={{ fontSize: 10, fontWeight: 700, color: "#0f172a", textTransform: "uppercase", marginBottom: 2 }}>
                                    Total
                                </div>
                                <div style={{ fontSize: 17, fontWeight: 900, color: "#0f172a" }}>{totalLeave}</div>
                                <div style={{ fontSize: 10, color: "#64748b" }}>days</div>
                            </div>
                        </Col>
                    </Row>

                    {/* Pie chart */}
                    <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                            <Pie
                                data={chartData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={100}
                                paddingAngle={3}
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                labelLine={false}
                            >
                                {chartData.map((_, i) => (
                                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip
                                formatter={(v, name, props) => [
                                    `${v} days`,
                                    props.payload?.fullName || name,
                                ]}
                                contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }}
                            />
                            <Legend
                                formatter={(name, entry) => entry.payload?.fullName || name}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </>
            )}
        </Card>
    );
}
